import { useState, useCallback, useEffect } from 'react';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Card,
  CardHeader,
  Button,
  Select,
  Badge,
  Pagination,
  LoadingSkeleton,
  Input,
  Modal,
} from '@nexora/design-system';
import {
  buildPurchaseOrderPayload,
  emptyLine,
  lineTotal,
  num,
  totals,
  type PurchaseOrderLine,
} from './purchaseOrderPayload';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useCreatePurchaseOrder,
  useApprovePurchaseOrder,
  useCancelPurchaseOrder,
  useSupplierLookup,
  useProductLookup,
  type PurchaseOrder,
  type PurchaseOrderInput,
} from './useProcurement';

/* Os valores sao os do backend (`PurchaseOrderStatus`). Estavam em minusculas
   e com nomes proprios ('pending'), entao o filtro nao filtrava nada e todo
   status caia no rotulo de fallback. */
const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PENDING_APPROVAL', label: 'Aguardando aprovação' },
  { value: 'APPROVED', label: 'Aprovado' },
  { value: 'IN_TRANSIT', label: 'Em trânsito' },
  { value: 'PARTIALLY_RECEIVED', label: 'Recebido parcial' },
  { value: 'RECEIVED', label: 'Recebido' },
  { value: 'REJECTED', label: 'Rejeitado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

function statusBadge(status: PurchaseOrder['status']) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    DRAFT: { variant: 'neutral', label: 'Rascunho' },
    PENDING_APPROVAL: { variant: 'warning', label: 'Aguardando aprovação' },
    APPROVED: { variant: 'success', label: 'Aprovado' },
    IN_TRANSIT: { variant: 'info', label: 'Em trânsito' },
    PARTIALLY_RECEIVED: { variant: 'info', label: 'Recebido parcial' },
    RECEIVED: { variant: 'success', label: 'Recebido' },
    REJECTED: { variant: 'error', label: 'Rejeitado' },
    CANCELLED: { variant: 'error', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'info', label: status };
}

export function PurchaseOrdersPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data, isLoading, error } = usePurchaseOrders({
    status: statusFilter,
    page,
    limit: 20,
  });

  const createMutation = useCreatePurchaseOrder();
  const approveMutation = useApprovePurchaseOrder();
  const cancelMutation = useCancelPurchaseOrder();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: PurchaseOrderInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Ordem de compra criada com sucesso.', 'success');
        setModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao criar ordem de compra.', 'error');
      }
    },
    [createMutation, showToast],
  );

  const handleApprove = useCallback(
    async (id: string) => {
      try {
        await approveMutation.mutateAsync(id);
        showToast('Ordem de compra aprovada com sucesso.', 'success');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao aprovar ordem de compra.', 'error');
      }
    },
    [approveMutation, showToast],
  );

  const handleCancel = useCallback(
    async (id: string) => {
      try {
        await cancelMutation.mutateAsync(id);
        showToast('Ordem de compra cancelada com sucesso.', 'success');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao cancelar ordem de compra.', 'error');
      }
    },
    [cancelMutation, showToast],
  );

  const handleNew = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const totalPages = data?.totalPages ?? 1;
  const orders = data?.data ?? [];

  return (
    <div className="po-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Ordens de Compra
            {data && <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">({data.total})</span>}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Ordens de compra, aprovação e recebimento.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={handleNew}>+ Nova OC</Button>
        </div>
      </div>

      {/* Filter */}
      <div className="po-page__filter-card">
        <Card padding="1rem" variant="outlined">
          <div className="po-page__filter-row">
            <div className="po-page__filter-select">
              <Select label="Filtrar por status" value={statusFilter} onChange={handleFilterChange}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </div>
          </div>
        </Card>
      </div>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'ordem' : 'ordens'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Ordens de compra</h3>
        </CardHeader>
        {isLoading && (
          <div className="po-page__loading">
            <LoadingSkeleton />
            <span className="po-page__loading-text">Carregando ordens de compra...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="po-page__error">
            <p className="po-page__error-title">Erro ao carregar ordens de compra</p>
            <p className="po-page__error-detail">{(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && orders.length === 0 && (
          <div className="po-page__empty">
            <p className="po-page__empty-text">Nenhuma ordem de compra encontrada.</p>
            <Button variant="outline" size="sm" onClick={handleNew}>Criar primeira OC</Button>
          </div>
        )}

        {!isLoading && !error && orders.length > 0 && (
          <div className="po-page__table-wrap">
            <table className="po-page__table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fornecedor</th>
                  <th>Emissão</th>
                  <th>Previsão</th>
                  <th className="right">Itens</th>
                  <th className="right">Total</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="po-row">
                    <td className="po-cell--number">{o.number}</td>
                    <td className="po-cell--supplier">
                      <span className="po-supplier-name">{o.vendorName}</span>
                      {o.vendorDocument && <span className="po-supplier-doc">{o.vendorDocument}</span>}
                    </td>
                    <td className="po-cell--date">{formatDate(o.createdAt)}</td>
                    <td className="po-cell--date">{formatDate(o.expectedDate)}</td>
                    <td className="po-cell--num">{o.items.length}</td>
                    <td className="po-cell--total">{formatCurrency(o.total)}</td>
                    <td className="po-cell--status">
                      <Badge variant={statusBadge(o.status).variant} size="sm" dot>{statusBadge(o.status).label}</Badge>
                    </td>
                    <td className="po-cell--actions">
                      <div className="po-actions">
                        <Button variant="ghost" size="sm" onClick={() => setDetailOrderId(o.id)}>Detalhes</Button>
                        {o.status === 'PENDING_APPROVAL' && (
                          <Button variant="ghost" size="sm" loading={approveMutation.isPending} onClick={() => handleApprove(o.id)}>Aprovar</Button>
                        )}
                        {(o.status === 'PENDING_APPROVAL' || o.status === 'APPROVED') && (
                          <Button variant="ghost" size="sm" loading={cancelMutation.isPending} onClick={() => handleCancel(o.id)} className="po-btn--cancel">Cancelar</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && totalPages > 1 && (
          <div className="po-page__pagination">
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <DetailModal
        isOpen={detailOrderId !== null}
        onClose={() => setDetailOrderId(null)}
        orderId={detailOrderId}
      />

      {/* Create Modal */}
      <CreateOrderModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
      />

      {/* Toast */}
      {toastMessage && (
        <div role="alert" className={`po-toast po-toast--${toastVariant}`}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar" className="po-toast__close">&times;</button>
        </div>
      )}

      <style>{`
        .po-page { padding: 1.5rem; }
        .po-cell--number { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .po-cell--num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .po-supplier-name { display: block; }
        .po-supplier-doc { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; font-weight: 400; }
        .po-sku { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .po-detail-sub { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }

        .po-fieldset { border: 1px solid #c4c6cf; border-radius: 4px; padding: 0.75rem 1rem 1rem; margin: 0 0 1rem; background: #ffffff; }
        .po-fieldset legend { padding: 0 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .po-vendor-grid { display: grid; grid-template-columns: 2fr 1.2fr 1.5fr auto; gap: 1rem; align-items: end; }
        .po-vendor-clear { display: flex; justify-content: flex-end; }
        .po-hint { margin: 0.5rem 0 0; font-size: 12px; color: #43474e; }

        .po-lookup { list-style: none; margin: 0.25rem 0 0; padding: 0; border: 1px solid #c4c6cf; border-radius: 4px; background: #ffffff; max-height: 220px; overflow-y: auto; position: relative; z-index: 2; }
        .po-lookup li + li { border-top: 1px solid #eceef0; }
        .po-lookup button { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; width: 100%; padding: 0.5rem 0.75rem; background: none; border: none; text-align: left; cursor: pointer; font: inherit; }
        .po-lookup button:hover { background: #f2f4f6; }
        .po-lookup__name { color: #191c1e; }
        .po-lookup__meta { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; white-space: nowrap; }
        .po-product-cell { min-width: 240px; position: relative; }
        .po-product-picked { background: none; border: none; padding: 0; font: inherit; color: #191c1e; text-align: left; cursor: pointer; text-decoration: underline dotted; }

        .po-summary { margin: 0; display: grid; gap: 0.375rem; max-width: 360px; margin-left: auto; }
        .po-summary > div { display: flex; justify-content: space-between; gap: 2rem; }
        .po-summary dt { color: #43474e; font-size: 0.875rem; }
        .po-summary dd { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .po-summary__total { border-top: 1px solid #c4c6cf; padding-top: 0.375rem; margin-top: 0.125rem; font-weight: 700; }
        .po-summary__total dt { color: #191c1e; font-weight: 600; }

        .po-form-row { display: grid; grid-template-columns: 200px 1fr; gap: 1rem; }
        /* Numero digitado alinhado a direita, como a coluna em que ele vive:
           quantidade e dinheiro se conferem pela ultima casa. */
        .po-items-table td.right input { text-align: right; }
        .po-page__filter-card { margin-bottom: 1rem; }
        .po-page__filter-row { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .po-page__filter-select { min-width: 220px; }
        .po-page__loading { display: flex; align-items: center; justify-content: center; padding: 3rem; gap: 0.75rem; color: #43474e; }
        .po-page__loading-text { font-size: 0.875rem; }
        .po-page__error { padding: 3rem 2rem; text-align: center; color: #ba1a1a; }
        .po-page__error-title { font-weight: 600; margin: 0 0 0.5rem; }
        .po-page__error-detail { font-size: 0.875rem; margin: 0; color: #74777f; }
        .po-page__empty { padding: 3rem 2rem; text-align: center; color: #74777f; }
        .po-page__empty-text { margin: 0 0 1rem; font-size: 0.875rem; }
        .po-page__table-wrap { overflow-x: auto; }
        .po-page__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .po-page__table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .po-page__table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; }
        .po-row:hover td { background: #eceef0; }
        .po-cell--supplier { font-weight: 500; }
        .po-cell--date { white-space: nowrap; }
        .po-cell--total { font-weight: 600; }
        .po-cell--actions { white-space: nowrap; }
        .po-actions { display: flex; gap: 0.25rem; align-items: center; }
        .po-btn--cancel { color: #ba1a1a; }
        .po-page__pagination { padding: 1rem; border-top: 1px solid #eceef0; }
        .po-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px; }
        .po-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .po-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .po-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
        .po-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.5rem; }
        .po-detail-label { margin: 0; font-size: 0.75rem; color: #74777f; }
        .po-detail-value { margin: 0.125rem 0 0; font-weight: 600; }
        .po-detail-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .po-detail-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .po-detail-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .po-detail-table th:is(.right), .po-detail-table td:is(.right) { text-align: right; }
        .po-form { display: flex; flex-direction: column; gap: 1rem; }
        .po-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1rem; }
        .po-items-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
        .po-items-title { font-size: 0.875rem; font-weight: 600; color: #191c1e; }
        .po-items-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .po-items-table th { text-align: left; padding: 0.5rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .po-items-table td { padding: 0.25rem 0.5rem; border-bottom: 1px solid #eceef0; }
        .po-items-table th:is(.right), .po-items-table td:is(.right) { text-align: right; }
        .po-items-table .subtotal-cell { padding: 0.5rem; text-align: right; font-weight: 600; color: #43474e; white-space: nowrap; }
        .po-items-table .remove-cell { padding: 0.25rem 0.5rem; text-align: center; }
        .po-items-total-row td { border-top: 2px solid #e0e3e5; }
        .po-items-total-label { padding: 0.5rem; text-align: right; font-weight: 700; font-size: 0.875rem; }
        .po-items-total-value { padding: 0.5rem; text-align: right; font-weight: 700; font-size: 0.875rem; }
        .po-remove-btn { background: none; border: none; color: #ba1a1a; cursor: pointer; font-size: 1rem; }
        .po-modal-section { margin-bottom: 0.5rem; padding: 0.75rem; background: #f2f4f6; border-radius: 4px; }
        .po-modal-section p { margin: 0; }
        .po-modal-section .supplier-name { font-size: 0.875rem; font-weight: 600; }
        .po-modal-section .meta { font-size: 0.75rem; color: #43474e; margin-top: 0.25rem; }
      `}</style>
    </div>
  );
}

/* ── Detail Modal ── */

function DetailModal({ isOpen, onClose, orderId }: {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
}) {
  const { data: order, isLoading, error } = usePurchaseOrder(orderId ?? '');

  const footer = (
    <Button variant="outline" onClick={onClose}>Fechar</Button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalhes da Ordem de Compra" footer={footer}>
      {isLoading && (
        <div className="po-page__loading">
          <LoadingSkeleton />
          <span>Carregando...</span>
        </div>
      )}
      {error && !isLoading && (
        <p style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>{(error as Error).message}</p>
      )}
      {order && !isLoading && (
        <div>
          <div className="po-detail-grid">
            <div>
              <p className="po-detail-label">Número</p>
              <p className="po-detail-value">{order.number}</p>
            </div>
            <div>
              <p className="po-detail-label">Fornecedor</p>
              <p className="po-detail-value">{order.vendorName}</p>
              <p className="po-detail-sub">{order.vendorDocument}</p>
            </div>
            <div>
              <p className="po-detail-label">Emissão</p>
              <p className="po-detail-value">{formatDate(order.createdAt)}</p>
            </div>
            <div>
              <p className="po-detail-label">Previsao</p>
              <p className="po-detail-value">{formatDate(order.expectedDate)}</p>
            </div>
            <div>
              <p className="po-detail-label">Status</p>
              <p className="po-detail-value">
                <Badge variant={statusBadge(order.status).variant} size="sm" dot>{statusBadge(order.status).label}</Badge>
              </p>
            </div>
            <div>
              <p className="po-detail-label">Subtotal</p>
              <p className="po-detail-value">{formatCurrency(order.subtotal)}</p>
            </div>
            <div>
              <p className="po-detail-label">Total</p>
              <p className="po-detail-value" style={{ fontWeight: 700 }}>{formatCurrency(order.total)}</p>
            </div>
          </div>

          <h3 className="font-headline-sm text-headline-sm text-on-surface mt-0 mb-sm">Itens</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="po-detail-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Produto</th>
                  <th className="right">Qtd</th>
                  <th className="right">V. Unitário</th>
                  <th className="right">Impostos</th>
                  <th className="right">Total</th>
                  <th className="right">Recebido</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.id}>
                    <td className="po-sku">{item.sku}</td>
                    <td style={{ fontWeight: 500 }}>{item.description}</td>
                    <td className="right">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="right">{formatCurrency(item.unitValue)}</td>
                    <td className="right">
                      {formatCurrency(Object.values(item.taxValues ?? {}).reduce((a, b) => a + b, 0))}
                    </td>
                    <td className="right" style={{ fontWeight: 600 }}>{formatCurrency(item.total)}</td>
                    <td className="right">{item.receivedQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Create Order Modal ── */

function CreateOrderModal({ isOpen, onClose, onSubmit, submitting }: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: PurchaseOrderInput) => Promise<void>;
  submitting: boolean;
}) {
  const [vendorId, setVendorId] = useState('');
  const [vendorLabel, setVendorLabel] = useState('');
  const [vendorDocument, setVendorDocument] = useState('');
  const [vendorContact, setVendorContact] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<PurchaseOrderLine[]>([emptyLine()]);
  const [activeLine, setActiveLine] = useState<number | null>(null);

  const suppliers = useSupplierLookup(vendorId ? '' : vendorSearch);
  const productSearch = activeLine !== null ? (items[activeLine]?.search ?? '') : '';
  const products = useProductLookup(productSearch);

  const addRow = useCallback(() => {
    setItems((prev) => [...prev, emptyLine()]);
  }, []);

  const removeRow = useCallback((idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateItem = useCallback((idx: number, patch: Partial<PurchaseOrderLine>) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, ...patch } : item)));
  }, []);

  const { subtotal, taxTotal, total } = totals(items);

  const pickSupplier = (s: {
    id: string;
    name: string;
    documentNumber?: string;
    email?: string;
    phone?: string;
    mobile?: string;
  }): void => {
    setVendorId(s.id);
    setVendorLabel(s.name);
    setVendorDocument(s.documentNumber ?? '');
    setVendorContact(s.email ?? s.phone ?? s.mobile ?? '');
    setVendorSearch('');
  };

  const clearSupplier = (): void => {
    setVendorId('');
    setVendorLabel('');
    setVendorDocument('');
    setVendorContact('');
  };

  const reset = (): void => {
    clearSupplier();
    setVendorSearch('');
    setExpectedDate('');
    setNotes('');
    setItems([emptyLine()]);
    setActiveLine(null);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const payload = buildPurchaseOrderPayload({
      vendorId,
      lines: items,
      ...(expectedDate ? { expectedDate } : {}),
      ...(notes ? { notes } : {}),
    });
    if (!payload) return;

    await onSubmit(payload);
    if (!submitting) reset();
  };

  const valid = !!vendorId && items.some((i) => i.productId && num(i.quantity) > 0);

  const footer = (
    <>
      <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
      <Button variant="primary" type="submit" form="create-po-form" loading={submitting} disabled={!valid}>
        Salvar pedido
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo pedido de compra" footer={footer} size="fullscreen">
      <form id="create-po-form" onSubmit={handleSubmit}>
        <div className="po-form">
          {/* ── Dados do fornecedor ── */}
          <fieldset className="po-fieldset">
            <legend>Dados do fornecedor</legend>
            {vendorId ? (
              <div className="po-vendor-grid">
                <div>
                  <p className="po-detail-label">Fornecedor</p>
                  <p className="po-detail-value">{vendorLabel}</p>
                </div>
                <div>
                  <p className="po-detail-label">CNPJ / CPF</p>
                  <p className="po-detail-value po-sku">{vendorDocument || '—'}</p>
                </div>
                <div>
                  <p className="po-detail-label">Contato</p>
                  <p className="po-detail-value">{vendorContact || '—'}</p>
                </div>
                <div className="po-vendor-clear">
                  <Button variant="outline" size="sm" type="button" onClick={clearSupplier}>
                    Trocar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Input
                  label="Fornecedor"
                  value={vendorSearch}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  placeholder="Buscar por nome ou documento (mín. 2 letras)"
                />
                {suppliers.isLoading && vendorSearch.length >= 2 && (
                  <p className="po-hint">Buscando...</p>
                )}
                {!suppliers.isLoading && vendorSearch.length >= 2 && (suppliers.data?.data.length ?? 0) === 0 && (
                  <p className="po-hint">Nenhum fornecedor encontrado.</p>
                )}
                {(suppliers.data?.data.length ?? 0) > 0 && (
                  <ul className="po-lookup">
                    {suppliers.data?.data.map((sup) => (
                      <li key={sup.id}>
                        <button type="button" onClick={() => pickSupplier(sup)}>
                          <span className="po-lookup__name">{sup.name}</span>
                          <span className="po-lookup__meta">{sup.documentNumber ?? '—'}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </fieldset>

          {/* ── Itens ── */}
          <fieldset className="po-fieldset">
            <legend>Itens do pedido</legend>
            <div className="po-items-header">
              <span className="po-items-title">
                {items.filter((i) => i.productId).length} item(ns) selecionado(s)
              </span>
              <Button variant="outline" size="sm" type="button" onClick={addRow}>+ Adicionar item</Button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="po-items-table">
                <thead>
                  <tr>
                    <th style={{ width: '110px' }}>SKU</th>
                    <th>Produto</th>
                    <th className="right" style={{ width: '90px' }}>Qtd</th>
                    <th className="right" style={{ width: '130px' }}>V. Unitário</th>
                    <th className="right" style={{ width: '100px' }}>ICMS (%)</th>
                    <th className="right" style={{ width: '100px' }}>IPI (%)</th>
                    <th className="right" style={{ width: '130px' }}>Total</th>
                    <th style={{ width: '40px' }} aria-label="Remover" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="po-sku">{item.sku || '—'}</td>
                      <td className="po-product-cell">
                        {item.productId ? (
                          <button
                            type="button"
                            className="po-product-picked"
                            onClick={() => updateItem(idx, { productId: '', sku: '', description: '', search: '' })}
                          >
                            {item.description}
                          </button>
                        ) : (
                          <>
                            <Input
                              label=""
                              value={item.search}
                              onFocus={() => setActiveLine(idx)}
                              onChange={(e) => {
                                setActiveLine(idx);
                                updateItem(idx, { search: e.target.value });
                              }}
                              placeholder="Buscar produto (mín. 2 letras)"
                            />
                            {activeLine === idx && (products.data?.length ?? 0) > 0 && (
                              <ul className="po-lookup">
                                {products.data?.map((p) => (
                                  <li key={p.id}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateItem(idx, {
                                          productId: p.id,
                                          sku: p.sku,
                                          description: p.name,
                                          search: '',
                                        })
                                      }
                                    >
                                      <span className="po-lookup__name">{p.name}</span>
                                      <span className="po-lookup__meta">{p.sku}</span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </td>
                      <td className="right">
                        <Input label="" type="number" min="0" step="1" value={item.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} placeholder="0" />
                      </td>
                      <td className="right">
                        <Input label="" type="number" min="0" step="0.01" value={item.unitValue} onChange={(e) => updateItem(idx, { unitValue: e.target.value })} placeholder="0,00" />
                      </td>
                      <td className="right">
                        <Input label="" type="number" min="0" step="0.01" value={item.icmsPercent} onChange={(e) => updateItem(idx, { icmsPercent: e.target.value })} placeholder="0" />
                      </td>
                      <td className="right">
                        <Input label="" type="number" min="0" step="0.01" value={item.ipiPercent} onChange={(e) => updateItem(idx, { ipiPercent: e.target.value })} placeholder="0" />
                      </td>
                      <td className="subtotal-cell">{formatCurrency(lineTotal(item))}</td>
                      <td className="remove-cell">
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeRow(idx)} className="po-remove-btn" aria-label="Remover item">&times;</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="po-hint">
              ICMS e IPI entram em percentual e são gravados em reais — é assim que o pedido
              guarda imposto por item.
            </p>
          </fieldset>

          {/* ── Resumo financeiro ── */}
          <fieldset className="po-fieldset">
            <legend>Resumo financeiro</legend>
            <dl className="po-summary">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatCurrency(subtotal)}</dd>
              </div>
              <div>
                <dt>Total de impostos</dt>
                <dd>{formatCurrency(taxTotal)}</dd>
              </div>
              <div className="po-summary__total">
                <dt>Total final</dt>
                <dd>{formatCurrency(total)}</dd>
              </div>
            </dl>
          </fieldset>

          <div className="po-form-row">
            <Input label="Data prevista" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Visível para o fornecedor" />
          </div>
        </div>
      </form>
    </Modal>
  );
}
